/**
 * KioskPlayMode
 *
 * Opens the slideshow for a selected community.
 * If the community has no saved setup, it shows a message with a
 * button to go to Settings.
 *
 * summary:
 * - Reads `communityId` from the URL
 * - Calls `getKioskConfigForPlayMode` to load saved config
 * - Shows `SlideshowManage` when config is available,
 *   otherwise shows `NoConfigurationMessage`
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getKioskConfigForPlayMode } from "../../services/api";
import NoConfigurationMessage from "./components/SlideShowUtilsComponents";
import SlideshowManage from "./components/Slides/SlideshowManage";

/**
 * KioskPlayMode Component
 *
 * @returns {JSX.Element} The kiosk play mode component
 */
export default function KioskPlayMode() {
  // Route parameters
  const { communityId } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [hasConfiguration, setHasConfiguration] = useState(false);

  // Fetch kiosk configuration on component mount
  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);

      try {
        console.log(
          `Fetching kiosk configuration for community: ${communityId}`
        );
        const data = await getKioskConfigForPlayMode(communityId);
        console.log("Kiosk Configuration Data:", data);

        if (data && data.config) {
          setConfig(data);
          setHasConfiguration(true);
          console.log("Configuration found, ready for slideshow display");
        } else {
          setHasConfiguration(false);
          console.log("No configuration found for this community");
        }
      } catch (err) {
        console.error("Error fetching kiosk configuration:", err);

        // Check if it's a 404 error (no configuration found)
        if (err?.response?.status === 404) {
          console.log("No configuration found for this community");
          setHasConfiguration(false);
        } else {
          // Handle other errors
          console.error("Other error occurred:", err);
          setHasConfiguration(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [communityId]);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading kiosk configuration...
      </div>
    );
  }

  // Render appropriate component based on configuration status
  if (!hasConfiguration) {
    return (
      <NoConfigurationMessage communityId={communityId} navigate={navigate} />
    );
  }

  return <SlideshowManage config={config} />;
}
